import Pill from 'ext-ui/components/Pill/Pill'
import moment from 'moment'
import React, { ReactElement, useCallback } from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import UsageMeterCard from 'src/app/ManageSubscription/components/UsageMeterCard/UsageMeterCard'
import { useSaasUpgradeAction } from 'src/hooks/useSaasUpgradeAction'
import { useTranslate } from 'src/localization/Trns'
import { getSaasTierLabel, SaasTierType } from 'src/models/organizations.models'
import {
  usePutManageSubscription,
  useQueryOrganizationUsages,
} from 'src/queries/organizations/saas.queries'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'

import style from './ManageSubscriptionUsageMeters.style'

interface ManageSubscriptionUsageMetersProps {
  organizationId: string
  currentTier: SaasTierType
  billingAnchorDate?: string
}

function getUpgradeTierLabel(currentTier: SaasTierType): string | null {
  switch (currentTier) {
    case 'STARTER':
      return 'Pro'
    case 'PRO':
      return 'Enterprise'
    default:
      return null
  }
}

export function getMonthlyUsageResetDate(
  billingAnchorDate: string | undefined,
  now: Date = new Date(),
): Date {
  const anchor = moment.parseZone(billingAnchorDate)
  const localReference = moment(now)

  if (!anchor.isValid()) {
    return localReference.clone().add(1, 'month').startOf('month').toDate()
  }

  const reference = localReference.clone().utcOffset(anchor.utcOffset())

  const getCandidate = (referenceMonth: moment.Moment): moment.Moment => {
    const candidate = anchor
      .clone()
      .year(referenceMonth.year())
      .month(referenceMonth.month())
      .date(1)

    const clampedDay = Math.min(anchor.date(), candidate.daysInMonth())

    return candidate.date(clampedDay)
  }

  const thisMonthCandidate = getCandidate(reference)

  if (thisMonthCandidate.isAfter(reference)) {
    return thisMonthCandidate.toDate()
  }

  return getCandidate(reference.clone().add(1, 'month')).toDate()
}

function getResetDateLabel(billingAnchorDate?: string): string {
  const resetDate = getMonthlyUsageResetDate(billingAnchorDate)
  return resetDate.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  })
}

function getActiveVirtualCardLimit(currentTier: SaasTierType): number | null {
  switch (currentTier) {
    case 'STARTER':
      return 100
    case 'PRO':
      return 500
    default:
      return null
  }
}

export default function ManageSubscriptionUsageMeters(
  props: ManageSubscriptionUsageMetersProps,
): ReactElement | null {
  const { organizationId, currentTier, billingAnchorDate } = props
  const translate = useTranslate()
  const { analytics } = useAnalyticsContext()

  const { enable_manage_plan_usage_meters: enabled } =
    useSelector(selectFeatureFlaggerFromState) || {}

  const { data: usages } = useQueryOrganizationUsages({
    orgId: organizationId,
    enabled: !!organizationId && !!enabled,
  })

  const upgradeAction = useSaasUpgradeAction(organizationId)
  const { mutate: putManageSubscription } = usePutManageSubscription()

  const handleUpgradeClick = useCallback((): void => {
    const nextTier = getUpgradeTierLabel(currentTier)
    if (!nextTier) return
    upgradeAction(
      nextTier.toUpperCase() as SaasTierType,
      'Manage Subscription - Usage Meter Upgrade',
    )
  }, [currentTier, upgradeAction])

  const handleAddSeatsClick = useCallback((): void => {
    putManageSubscription(
      { orgId: organizationId, redirect: false, body: {} },
      {
        onSuccess: ({ url }) => {
          if (url) {
            window.open(url, '_blank', 'noreferrer')
          }
        },
      },
    )
  }, [organizationId, putManageSubscription])

  if (!enabled || currentTier === 'ENTERPRISE') return null

  const activeCards = usages?.features?.ACTIVE_VIRTUAL_CARD_LIMIT
  const createdCards = usages?.features?.VIRTUAL_CARD_LIMIT
  const orgMembers = usages?.features?.ORGANIZATION_TOTAL_SIZE

  const upgradeTierLabel = getUpgradeTierLabel(currentTier)
  const tierLabel = getSaasTierLabel(currentTier)
  const activeCardLimit = getActiveVirtualCardLimit(currentTier)
  const activeCardEntitlement = activeCards
    ? (activeCardLimit ?? activeCards.entitlement)
    : 0

  const resetLabel = translate('resets ${date}', {
    date: getResetDateLabel(billingAnchorDate),
  })

  const trackEvent = (
    action: string,
    meter: string,
    usage: number,
    limit: number,
  ): void => {
    analytics?.track('usage meter', {
      action,
      meter,
      usage,
      limit,
      account_type: currentTier,
    })
  }

  return (
    <div
      className="ManageSubscriptionUsageMeters"
      css={style}
      data-testid="manage-subscription-usage-meters"
    >
      <div className="ManageSubscriptionUsageMeters-header">
        <span className="font-h3">{translate('Usage')}</span>
        <Pill color={'new'}>{'★ New'}</Pill>
      </div>
      <div className="ManageSubscriptionUsageMeters-cards">
        {activeCards && (
          <UsageMeterCard
            title={translate('VIRTUAL CARDS ACTIVE')}
            usage={activeCards.usage}
            limit={activeCardEntitlement}
            sublabel={translate('${tier} limit: ${limit}', {
              tier: tierLabel,
              limit: String(activeCardEntitlement),
            })}
            tooltipContent={translate(
              'Number of virtual cards currently active in your organization.',
            )}
            ctaLabel={
              upgradeTierLabel
                ? translate('Upgrade to ${tier} →', { tier: upgradeTierLabel })
                : undefined
            }
            onCtaClick={
              upgradeTierLabel
                ? (): void => {
                    trackEvent(
                      'cta clicked',
                      'vcns_active',
                      activeCards.usage,
                      activeCardEntitlement,
                    )
                    handleUpgradeClick()
                  }
                : undefined
            }
            onTooltipHover={(): void => {
              trackEvent(
                'tooltip hovered',
                'vcns_active',
                activeCards.usage,
                activeCardEntitlement,
              )
            }}
          />
        )}
        {createdCards && (
          <UsageMeterCard
            title={translate('VIRTUAL CARDS CREATED')}
            usage={createdCards.usage}
            limit={createdCards.entitlement}
            sublabel={translate('This billing month')}
            footerSuffix={resetLabel}
            tooltipContent={translate(
              'Number of virtual cards created during the current billing month.',
            )}
            ctaLabel={
              upgradeTierLabel
                ? translate('Upgrade to ${tier} →', { tier: upgradeTierLabel })
                : undefined
            }
            onCtaClick={
              upgradeTierLabel
                ? (): void => {
                    trackEvent(
                      'cta clicked',
                      'vcns_created',
                      createdCards.usage,
                      createdCards.entitlement,
                    )
                    handleUpgradeClick()
                  }
                : undefined
            }
            onTooltipHover={(): void => {
              trackEvent(
                'tooltip hovered',
                'vcns_created',
                createdCards.usage,
                createdCards.entitlement,
              )
            }}
          />
        )}
        {orgMembers && (
          <UsageMeterCard
            title={translate('ORG MEMBERS')}
            usage={orgMembers.usage}
            limit={orgMembers.entitlement}
            limitSuffix={translate('seats')}
            tooltipContent={translate(
              'Number of members in your organization vs your plan limit.',
            )}
            ctaLabel={translate('Add more seats →')}
            onCtaClick={(): void => {
              trackEvent(
                'cta clicked',
                'org_members',
                orgMembers.usage,
                orgMembers.entitlement,
              )
              handleAddSeatsClick()
            }}
            onTooltipHover={(): void => {
              trackEvent(
                'tooltip hovered',
                'org_members',
                orgMembers.usage,
                orgMembers.entitlement,
              )
            }}
          />
        )}
      </div>
    </div>
  )
}
